import AnimatedNumber from '@components/Animation/AnimatedNumber';
import RainbowLoader from '@components/Loaders/RainbowLoader';
import { CardTitle, GridCard, SysMsg } from '@components/GridCards/Common.styled';
import { useStore } from '@store/appStore';
import db from '@utils/db';
import { useI18n } from '@i18n/useI18n';
import { useEffect, useRef, useState } from 'react';
import BackgroundGraph from '../BackgroundGraph';
import {
    Content,
    Course,
    CourseContent,
    CourseContentWrapper,
    CoursePending,
    CoursePercentage,
    CourseTop,
    ShowCourses
} from './CourseAbsence.styled';
import { bgColor } from '@models/RegistrationColors';
import toTitleCase, { normalizeName } from '@utils/textTools';
import Tooltip from '@components/Tooltips/Tooltip';
import SortOptions from "@components/GridCards/SortOptions";
import { AttendanceColors, calculatePercentage, ShowabsenceOrAttendance } from '@utils/percentageTools';
import UseOnScreen from '@components/Utils/useOnScreen';

interface IProps {
    userId?: string,
    selectCourse?: (id, name, displayname) => any,
    courseLoading?: boolean
    updateTime?: any
    guar?:boolean,
}

export const CourseAbsence = (props: IProps) => {
    const i18n = useI18n();
    const { filterDate, settings, setAdminSelection, callNameSetting } = useStore();
    const [show, setShow] = useState(false)
    const [courseList, setCourseList] = useState([])
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [sort, setSort] = useState("course A-Z")
    const ref = useRef<HTMLDivElement>(null);
    const isVisible = UseOnScreen(ref);
    const loadingData = useRef(false)
    // Only the newest GetChart run may update state; older runs (filter/student change, refresh) bail out.
    const runId = useRef(0)
    const loadedKey = useRef<string>(null)

    useEffect(() => {
        if (isVisible && !loadingData.current) {
            {
                loadingData.current = true
                GetChart()
            }
        }
    }, [props.userId, isVisible])

    useEffect(() => {
        if (loadingData.current) {
            loadingData.current = true
            GetChart();
        }
    }, [filterDate, props.userId, props.updateTime])



    const GetChart = async () => {
        const my = ++runId.current
        const isStale = () => my !== runId.current

        // A refresh of the same student/period keeps the current cards on screen until the first
        // new one arrives; a different student or period starts from an empty list.
        const key = `${props.userId}|${filterDate.from}|${filterDate.to}`
        if (loadedKey.current !== key) {
            loadedKey.current = key
            setCourseList([])
            setLoading(true)
        }
        setLoadingMore(true)

        try {
            const coursesData = await db.Student_Stat_GetAllCoursesClassIDs(props.userId, filterDate.from, filterDate.to);
            if (isStale()) return
            // No answer (e.g. the connection dropped): keep what's shown - the profile's update check
            // reloads the cards once the connection is back. `finally` below clears the loading state.
            if (!coursesData) return
            let studentInfo = await db.getUserInfo(props.userId)
            if (studentInfo?.length > 0) studentInfo = studentInfo[0]
            let meritData = []
            if (settings.USEMERIT && studentInfo?.student_id) meritData = (await db.ListCoursesForStudent(studentInfo.student_id, 30)) ?? []
            if (isStale()) return

            let tempL = []
            let courseIds = []
            for (let i = 0; i < coursesData.length; i++) {
                if (!courseIds.some(c => c === coursesData[i].class_id) && !meritData.some(c => c.sis_id === coursesData[i].class_id && c.coursedescription === null && c.active_merit)) courseIds.push(coursesData[i].class_id)
            }

            let coursePause = []

            if (settings.USENOTFULLBLOCKPAUSE) {
                let id = await db.getUserInfo(props.userId)
                if (id?.length > 0 && id[0].student_id) coursePause = (await db.GetPause4StudentPerClass(id[0].student_id, filterDate.from, filterDate.to)) ?? []
                if (isStale()) return
            }

            // One course at a time: each card is shown as soon as its own data is ready.
            for (let i = 0; i < courseIds.length; i++) {
                let courseDetails = await db.Student_Stat_GetCourseDetails(courseIds[i], props.userId, filterDate.from, filterDate.to)
                if (isStale()) return
                if (!courseDetails) continue // no answer for this course - skip its card rather than stop

                if (settings.USENOTFULLBLOCKPAUSE) {
                    let cP = coursePause.find(c => c.class_id === courseIds[i])
                    if (courseDetails.length > 0 && cP) {
                        //@ts-ignore
                        courseDetails[0].duration = courseDetails[0].duration - cP.pause_length
                        //@ts-ignore
                        courseDetails[0].absenceduration = courseDetails[0].absenceduration - cP.pause_absence_length
                        //@ts-ignore
                        courseDetails[0].merit = true
                    }
                }

                let entries = []
                courseDetails.forEach(c => {
                    if (!meritData.some(course => course.sis_id === courseIds[i] && course.coursedescription === c.course_name && course.active_merit)) {
                        let course = coursesData.find(course => course.coursedescription === c.course_name && course.class_id === courseIds[i])
                        if (course) entries.push(Object.assign(c, course))
                    }
                })

                for (const info of entries) {
                    let tempCourseList = await db.Student_Stat_CourseChartOverTime(courseIds[i], props.userId, info.coursedescription, filterDate.from, filterDate.to)
                    if (isStale()) return
                    let temp = [];

                    if (settings.USE_LESSON_BASED_ABSENCE === 'true') {
                        temp = (tempCourseList ?? []).map(d => d.lessonspercentage)
                    } else {
                        let count = 0;
                        let maxCount = Math.floor(tempCourseList?.length / 7);
                        for (let j = 0; j < tempCourseList?.length; j++) {
                            if (count > maxCount) {
                                count = 0;
                            }
                            if (count === 0) {
                                if (j + maxCount > tempCourseList.length) {
                                    temp.push(tempCourseList[tempCourseList.length - 1].minutespercentage)
                                } else {
                                    temp.push(tempCourseList[j].minutespercentage)
                                }
                            }
                        }
                    }
                    tempL.push({ classId: courseIds[i], merit: info.merit, course: info.coursedisplayname ? info.coursedisplayname : "", sCourse: info.coursedescription, total: info.lessons, missed: info.absencelessons, percentage: settings.USE_LESSON_BASED_ABSENCE === 'true' ? info.lessonpercentage : info.minutepercentage, dataPoints: temp, duration: info.duration, absenceduration: info.absenceduration })
                    setCourseList([...tempL])
                    setLoading(false)
                }
            }

            // Covers the "no courses" case and drops old cards left over from a refresh.
            setCourseList(tempL)
        } finally {
            if (!isStale()) {
                setLoading(false)
                setLoadingMore(false)
            }
        }
    }

    const handleBackground = (percentage: number) => {
        return AttendanceColors(settings.COLORSETTINGS, percentage)
    }


    const getSortedCourseList = () => {
        let tempList = JSON.parse(JSON.stringify(courseList))

        switch (sort) {
            case "% desc":
                tempList = tempList.sort((a, b) => {
                    if (a.percentage < b.percentage) return 1; else if (a.percentage === b.percentage) return 0; else if (a.percentage > b.percentage) return -1
                }
                )
                break;
            case "% asc":
                tempList = tempList.sort((a, b) => {

                    if (a.percentage < b.percentage) return -1; else if (a.percentage === b.percentage) return 0; else if (a.percentage > b.percentage) return 1

                })
                break;
            case "course A-Z":
                tempList = tempList.sort((a, b) => {

                    var o1 = a.course?.toLowerCase();
                    var o2 = b.course?.toLowerCase();

                    var p1 = a.sCourse?.toLowerCase();
                    var p2 = b.sCourse?.toLowerCase();

                    if (o1 < o2) return -1;
                    if (o1 > o2) return 1;
                    if (p1 < p2) return -1;
                    if (p1 > p2) return 1;
                    return 0;

                })
                break;
            case "course Z-A":
                tempList = tempList.sort((a, b) => {
                    var o1 = a.course?.toLowerCase();
                    var o2 = b.course?.toLowerCase();

                    var p1 = a.sCourse?.toLowerCase();
                    var p2 = b.sCourse?.toLowerCase();

                    if (o1 > o2) return -1;
                    if (o1 < o2) return 1;
                    if (p1 > p2) return -1;
                    if (p1 < p2) return 1;
                    return 0;
                })
                break;
            default:
                break;
        }

        return tempList
    }

    const getTime = (time: number) => {
        let minutes = time / 60
        let hours = Math.floor(minutes / 60)
        let timeString = hours + i18n.t('hour') + Math.floor(minutes % 60) + i18n.t('minute')
        return timeString
    }

    return (
        <GridCard ref={ref} col={4} background={"transparent"} heightAuto={true} padding={"0"} style={props.guar?{order:"999"}:null}>
            <CourseTop>
                <CardTitle>{i18n.t("course")}</CardTitle>
                <SortOptions setSort={setSort} course={true} />
            </CourseTop>

            {
                <Content>
                    {loading ? <RainbowLoader /> :
                        courseList.length > 0 ? <>{getSortedCourseList().map((c, index) => {
                            return <Course key={"c_" + c.classId + "_" + c.sCourse + "_" + c.course}>
                                <CourseContentWrapper>
                                    <CourseContent>
                                        <span><Tooltip placement={"top-center"} content={c.course}>{normalizeName(c.course, 20)}</Tooltip></span>
                                        <span>{i18n.t(settings.SHOWPRESENCE ? "time-attended" : "time-missed")}: <b>{settings.SHOWPRESENCE ? getTime(c.duration - c.absenceduration) : getTime(c.absenceduration)}</b></span>
                                        <span><Tooltip placement={"top-center"} content={c.sCourse}>{normalizeName(c.sCourse, 20)}</Tooltip></span>
                                        <span>{i18n.t("total-time")}: <b>{getTime(c.duration)}</b></span>
                                    </CourseContent>
                                    <BackgroundGraph dataPoints={c.dataPoints} height={20} />
                                </CourseContentWrapper>

                                <CoursePercentage backgroundColor={c.dataPoints?.length > 0 ? bgColor[handleBackground(c.percentage)] : "#959595"}>
                                    <span><AnimatedNumber value={ShowabsenceOrAttendance(settings.SHOWPRESENCE, c.merit ? calculatePercentage(c.duration, c.absenceduration) : c.percentage)}>%</AnimatedNumber></span>
                                </CoursePercentage>
                            </Course>
                        })}{loadingMore ? <CoursePending key="pending"><RainbowLoader /></CoursePending> : null}</> : (
                            <SysMsg>{i18n.t('no-courses-found')}</SysMsg>
                        )
                    }
                </Content>
            }

        </GridCard>
    );
};

export default CourseAbsence;
